import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Handshake, Globe, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import CaptchaChallenge from '../components/CaptchaChallenge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnersPage() {
  const { t, getLocalizedContent } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleCaptchaValidate = (isValid, token) => {
    setCaptchaValid(isValid);
    setCaptchaToken(token);
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaValid) {
      toast.error('Veuillez compléter la vérification anti-spam');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/partnership-requests`, {
        ...formData,
        timestamp: Math.floor(Date.now() / 1000),
        captcha_token: captchaToken
      });
      toast.success('Demande de partenariat envoyée avec succès !');
      setFormData({ company_name: '', contact_name: '', email: '', phone: '', message: '' });
      setCaptchaValid(false);
      setCaptchaToken(null);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="partners-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Partenariats
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('partners_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              {t('partners_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#0F172A] font-['Montserrat']">
              Nos partenaires de confiance
            </h2>
            <p className="text-gray-600 mt-2">
              Des collaborations stratégiques pour mieux vous servir
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Nos partenaires seront bientôt affichés ici
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {partners.map((partner, index) => (
                <motion.div
                  key={partner.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full border-0 shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`partner-card-${index}`}>
                    <CardContent className="p-6 text-center">
                      <div className="h-20 flex items-center justify-center mb-4">
                        {partner.logo_url ? (
                          <img
                            src={partner.logo_url}
                            alt={partner.name}
                            className="max-h-16 max-w-full object-contain"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-[#0B6AB3]/10 rounded-full flex items-center justify-center">
                            <Handshake className="h-8 w-8 text-[#0B6AB3]" />
                          </div>
                        )}
                      </div>
                      <h3 className="font-semibold text-lg text-[#0F172A] mb-2">
                        {partner.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {getLocalizedContent(partner.description)}
                      </p>
                      {partner.website && (
                        <a
                          href={partner.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-[#0B6AB3] text-sm font-medium hover:underline"
                        >
                          <Globe className="h-4 w-4 mr-1" />
                          Visiter le site
                        </a>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Partnership Request Form */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#0F172A] font-['Montserrat']">
              {t('partners_become')}
            </h2>
            <p className="text-gray-600 mt-2">
              Rejoignez notre réseau de partenaires et développons ensemble des opportunités
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Card className="border-0 shadow-lg" data-testid="partnership-form">
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="company_name">Nom de l'entreprise *</Label>
                      <Input
                        id="company_name"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        required
                        className="mt-1"
                        data-testid="partner-company"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_name">Nom du contact *</Label>
                      <Input
                        id="contact_name"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        required
                        className="mt-1"
                        data-testid="partner-contact"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email">{t('contact_email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="mt-1"
                        data-testid="partner-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">{t('contact_phone')} *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="mt-1"
                        data-testid="partner-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Décrivez votre proposition de partenariat *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="mt-1"
                      placeholder="Présentez votre entreprise et les opportunités de collaboration..."
                      data-testid="partner-message"
                    />
                  </div>
                  {/* Captcha Challenge */}
                  <CaptchaChallenge onValidate={handleCaptchaValidate} />
                  {/* Honeypot */}
                  <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} />
                  <Button
                    type="submit"
                    className="w-full bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
                    disabled={submitting || !captchaValid}
                    data-testid="partner-submit"
                  >
                    {submitting ? 'Envoi en cours...' : (
                      <>
                        Envoyer la demande
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
